/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import { Singleflight } from '@zcong/singleflight';
import AssertionError from 'assertion-error';
import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import { isMapSubset } from '../../../util.type';
import AssetDownloader, {
  getHeadersForMedium
} from '../../asset/downloader/asset-downloader';
import ErrorName from '../../error/error-name';
import FileSystemSpaceDetector from '../../file/system/file-system-space-detector';
import FileSystemUtil from '../../file/system/file-system-util';
import FileTransferUtil from '../../file/transfer/file-transfer-util';
import { getUri, linkAsset } from '../../medium/medium';
import PromiseUtil from '../../promise/promise-util';

/**
 * @import { DownloadCallbacks } from '.'
 * @import { ReadonlyIdMap } from '../../../util.type'
 * @import { Asset } from '../../asset/asset'
 * @import { IAssetDownloader } from '../../asset/downloader/asset-downloader'
 * @import BasePathResolver from '../../basepath/basepath'
 * @import Cleaner from '../../cleaner/cleaner'
 * @import { EventBus as IEventBus } from '../../event-bus'
 * @import FileSystemUnitFinder from '../../file-system-unit-finder/file-system-unit-finder'
 * @import { FileSystemManager, FileSystemUnit } from '../../file/system/'
 * @import { Logger } from '../../logger/logger'
 * @import { LogicalUnitMapper } from '../../logical-unit-mapper/'
 * @import { Medium } from '../../medium/medium'
 * @import { Session } from '../../session/session'
 * @import Store from '../../store/store'
 * @import { ViewerRegistry } from '../../viewer/registry/'
 * @import { ContentInfo } from '../info/content-info'
 */

/**
 * @typedef {Pick<ContentInfo, 'communicatorSettings' | 'media'>} IContentInfo
 * @typedef {IEventBus<EventTypes>} EventBus
 * @typedef {Pick<Session, 'storage'>} ISession
 * @typedef {{ medium: Medium; extract: boolean; checkVersion: boolean; offlineable: boolean; }} MediumToDownload
 * @typedef {typeof import('../../../config').default} Config
 */

// TODO #84 Přerušení stahování.
// - Downloader#cancel()
// - Zruší probíhající stahování.
// - Zde by se mělo pokusit přerušit aktuální stahování. Každopádně přerušit sérii.
// - Neměl by být vstupem downloadPromise, pokud by bylo v budoucnu více paralelních stahování?
// - Možná inspirace rozhraním $timeout: https://docs.angularjs.org/api/ng/service/$timeout
/**
 * Stahovač obsahu
 * @implements {EventBus}
 */
export default class ContentDownloader {
  /**
   * @param {IAssetDownloader} assetDownloader
   * @param {BasePathResolver} basePathResolver
   * @param {Cleaner} cleaner
   * @param {Config} config Konfigurace aplikace.
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemUnitFinder} fileSystemUnitFinder
   * @param {Logger} logger
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {Store} store Perzistentní read-write datový sklad s assety.
   * @param {ISession} session Sezení s nastavením zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   */
  constructor(
    assetDownloader,
    basePathResolver,
    cleaner,
    config,
    fileSystemManager,
    fileSystemUnitFinder,
    logger,
    logicalUnitMapper,
    store,
    session,
    viewerRegistry
  ) {
    this.assetDownloader = assetDownloader;
    this.basePathResolver = basePathResolver;
    this.cleaner = cleaner;
    // TODO: Chtělo by předávat jen default size a nikoliv celý Config objekt!
    this.defaultSize = config.device.downloader.defaultSize;
    this.fileSystemManager = fileSystemManager;
    this.fileSystemUnitFinder = fileSystemUnitFinder;
    this.logger = logger;
    this.logicalUnitMapper = logicalUnitMapper;
    this.session = session;
    this.store = store;
    this.viewerRegistry = viewerRegistry;

    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    this.sf = new Singleflight();
  }

  // TODO: Je otázka, když se tiše spolykají chyby, tak zda by nebylo lepší
  // vracet seznam stažených assetů a nestažených s patřičnými chybami.
  // Samozřejmě se stávající sémantikou, že chyba jednoho média nerejectne
  // tuto Promise jako celek a že se nic nezkouší znovu.
  // Tím pádem by bylo na volajícícm, co si s danými chybami udělá.
  /**
   * Stáhne soubory z nového obsahu.
   * Při paralelním stahování volání této funkce, může dojít ke konfliktům
   * jako např. paralelní stahování stejného souboru do stejného souboru.
   * V tomto případě je nutné volat funkci sériově.
   * Paralelní volání funkce je dovoleno pouze pro media v dávce,
   * která mají checkVersion == false.
   * @param {IContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @param {Object} [opts]
   * @param {ReadonlyIdMap<Medium>} [opts.media] Podmožina medii, která jsou v ContentInfo.media.
   * @param {DownloadCallbacks} [opts.downloadCallbacks] Callbacky informující o probíhajícím stahování.
   * @returns {Promise<Asset[]>} Seznam stažených assetů.
   */
  async download(
    contentInfo,
    { media = contentInfo.media, downloadCallbacks } = {}
  ) {
    if (!isMapSubset(media, contentInfo.media)) {
      throw new AssertionError('media is not subset of ContentInfo.media');
    }
    // TODO #84 Zvážit, jak signalizovat přerušení stahování:
    // - Místo assetIdArray vracet null.
    //   - Ignorovat rozestahované soubory?
    // - Vracet assetIdArray a příznak přerušení.
    //   - Rodič rozhodne co se soubory při přerušení.
    this.logger.debug('Downloader#download');

    /** @type {Asset[]} */
    const assetArray = [];
    /** @type {Error[]} */
    const downloadAssetErrors = [];

    // TODO: Kontrolovat přítomnost assetů v úložišti, kdyby náhodou již neexistovaly.
    const mediaToDownload = filterMediaToDownload(media, this.viewerRegistry);
    // Media bez assetů.
    const mediaWithoutAsset = filterMediaWithoutAssets(mediaToDownload);

    // Media, která mají stažený asset a je u nich vyžadována kontrola verze.
    const mediaToCheckVersion =
      filterMediaWithAssetAndCheckVersion(mediaToDownload);

    // TODO: Ideální by bylo udělat HEAD pro všechny media (to zatím nechceme), která se budou stahovat kvůli tomu,
    // aby se získala skutečná velikost souboru pro kontrolu místa na disku.
    const outdatedMedia = await mustFilterOutdatedMedia(
      mediaToCheckVersion,
      this.logger
    );

    const downloadMediaFunc = [
      // Media, která nemají asset se musí stáhnout.
      ...mediaWithoutAsset,
      // Media, která nejsou aktuální.
      ...outdatedMedia
    ].map(({ medium, extract, checkVersion }, idx) => async () => {
      try {
        /** @type {Asset | undefined} */
        let asset;
        if (checkVersion) {
          // Pokud má medium kontrolovat verzi, tak není žádoucí aby požadavky
          // na stahování deduplikovali pomocí single flight.
          asset = await this.downloadAsset(contentInfo, medium, { extract });
        } else {
          if (medium.asset === undefined) {
            // Medium nemá asset, takže se zkusí jestli se pořadí najít již stažený asset.
            asset = await AssetDownloader.findAsset(
              this.store,
              getUri(medium),
              { checksum: medium.checksum }
            );
          }
          if (!asset) {
            // Pokud nebyl nalezen žádný stažený asset, pokusíme se stáhnout nový.
            // U media bez kontroly verze je možné, že se bude stahovat stejný soubor vícekrát,
            // proto se provede deduplikace.
            asset = await this.sf.do(medium.url, () =>
              this.downloadAsset(contentInfo, medium, { extract })
            );
          }
        }

        // Navázání média na stažený asset.
        linkAsset(medium, asset);

        // Úspěšně stažený asset se přidá do výstupu.
        assetArray.push(asset);
      } catch (error) {
        // TODO #84 Zde by se mělo kontrolovat, jestli nebylo vyžádáno přerušení.
        // Pokud něco selže, tak se přeskočí na další médium.

        // Nenalezení jednotky nechceme logovat
        if (/** @type {Error} */ (error).name !== ErrorName.NotFoundError) {
          const downloadAssetError = new Error(
            `${medium.id}: ${medium.url}: ${String(error)}`
          );
          // V tomto případě nechceme posílat stack trace.
          downloadAssetError.stack = '';
          downloadAssetErrors.push(downloadAssetError);
        }
      } finally {
        // Vyvolání události po zpracování media.
        downloadCallbacks?.onProgress(idx + 1);
      }
    });

    if (downloadMediaFunc.length === 0) {
      return [];
    }

    downloadCallbacks?.onStart(downloadMediaFunc.length);
    // Sériové stažení assetů pro jednotlivá média.
    await PromiseUtil.allSerial(downloadMediaFunc);

    downloadCallbacks?.onComplete();

    if (downloadAssetErrors.length !== 0) {
      this.logger.error(
        new AggregateError(
          downloadAssetErrors,
          'some assets failed to download'
        )
      );
    }

    return assetArray;
  }

  /**
   * Stáhne jeden soubor z nového obsahu.
   * @private
   * @param {IContentInfo} contentInfo
   * @param {Medium} medium
   * @param {Object} opts
   * @param {boolean} opts.extract Určuje, jestli se má extrahovat obsah z archivu.
   * @returns {Promise<Asset>}
   */
  async downloadAsset(contentInfo, medium, opts) {
    const { extract } = opts;
    const remoteUrl = medium.url;
    try {
      const { defaultSize } = this;
      this.logger.debug(`Downloader#downloadAsset start ${remoteUrl}`);
      const logicalUnit = await FileSystemUtil.find(
        this.fileSystemManager,
        this.fileSystemUnitFinder,
        this.logicalUnitMapper,
        // TODO: DRY, Device, Downloader, ReportManager
        contentInfo.communicatorSettings.get('storage.types') ??
          this.session.storage
      );
      if (isNil(logicalUnit)) {
        const error = new Error('No unit found.');
        error.name = ErrorName.NotFoundError;
        throw error;
      }
      const logicalPath = await this.basePathResolver.resolve(logicalUnit);
      const fileSystemUnit = await this.logicalUnitMapper.toFileSystemUnit(
        logicalUnit
      );
      // Detekce dostatečného místa k uložení souboru.
      // Zatím se používá pouze výchozí hodnota z konfigurace.
      // Nezjišťuje se skutečná velikost souboru.
      let notEnoughtSpace = await shouldCleanSpace(defaultSize, fileSystemUnit);
      if (notEnoughtSpace) {
        // Pokud je obsazeno více než 90 % úložiště nebo
        // není dostatek místa.
        // Před začátkem čištění je potřeba zastavit aktuálně (původní ContentInfo) přehrávaný obsah,
        // aby se mohl stáhnout nový (nové ContentInfo).
        this.emitter.emit('cleanerBeforeRemoveSomeAssets');
        await this.cleaner.clean(contentInfo);

        notEnoughtSpace = await shouldCleanSpace(defaultSize, fileSystemUnit);
        if (notEnoughtSpace) {
          // Pokud ani po uvolnění není dostatek místa, vyhodí se chyba,
          // která se zaloguje.
          throw new Error(
            `unable to clean space for unit ${fileSystemUnit.getName()}`
          );
        }
      }
      // Uvolnil se dostatek místa, soubor lze stáhnout.
      // Persistentní uložení souboru do hlavního datového skladu.
      return this.assetDownloader.download(logicalPath, medium, { extract });
    } finally {
      this.logger.debug(`Downloader#downloadAsset end ${remoteUrl}`);
    }
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }
}

/**
 * @typedef {Object} EventTypes
 * @property {CleanerBeforeRemoveSomeAssetsEventListener} cleanerBeforeRemoveSomeAssets
 * @property {DownloadStartEventListener} start
 * @property {DownloadFinishedEventListener} finished
 * @property {DownloadProgressEventListener} progress
 */

/**
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @callback CleanerBeforeRemoveSomeAssetsEventListener
 * @returns {void}
 */

/**
 * @callback DownloadStartEventListener
 * @param {number} total
 * @returns {void}
 */

/**
 * @callback DownloadFinishedEventListener
 * @returns {void}
 */

/**
 * @callback DownloadProgressEventListener
 * @param {number} total
 * @param {number} progress
 * @returns {void}
 */

/**
 * @param {Medium} medium
 * @returns {Promise<Response>}
 */
async function headRequestMedium(medium) {
  const headers = getHeadersForMedium(medium);

  const getRequest = FileTransferUtil.createGetRequest(medium.url, {
    headers
  });
  const headRequest = await FileTransferUtil.createHeadRequest(getRequest);
  const response = await FileTransferUtil.fetchHeadBeforeDownload(headRequest);
  return response;
}

/**
 * Vyfiltruje jen ty media, které jsou stažitelná a nejsou stažena,
 * nebo jsou stažitelná a mají zapnutou kontrolu verze.
 * @param {ReadonlyMap<string, Medium>} media
 * @param {ViewerRegistry} viewerRegistry
 * @returns {MediumToDownload[]}
 */
export function filterMediaToDownload(media, viewerRegistry) {
  return Array.from(media.values())
    .map((medium) => {
      const viewerType = viewerRegistry.find(medium.type);
      if (viewerType === null) {
        // Media, která neumíme zobrazit, tak není potřeba stahovat.
        return undefined;
      }
      const extract = viewerType.isExtractable();
      const checkVersion = viewerType.getCheckFile(medium);
      const offlineable = viewerType.isOfflineable();
      if (
        // Medium je stažitelné.
        offlineable &&
        // Medium není stažené a nebo je vyžadována kontrola
        (isNil(medium.asset) || checkVersion)
      ) {
        return { medium, extract, checkVersion, offlineable };
      }
      return undefined;
    })
    .filter((item) => item !== undefined);
}

/**
 * Vyfiltruje media bez assetů.
 * @param {MediumToDownload[]} media
 * @returns {MediumToDownload[]}
 */
export function filterMediaWithoutAssets(media) {
  return media.filter(({ medium }) => isNil(medium.asset));
}

/**
 * Vyfiltruje media s assety a zapnutou volnou checkVersion. Odflitrují se media s duplicitní URL.
 * Všechny widgety mají tuto volbu zapnutou.
 * @param {MediumToDownload[]} media
 * @returns {MediumToDownload[]}
 */
export function filterMediaWithAssetAndCheckVersion(media) {
  return media.filter(
    ({ medium, checkVersion }) => medium.asset && checkVersion
  );
}

/**
 * Provede HEAD request na všechna media, a vyfiltruje jen ty,
 * která jsou zastarlá tj. vrací 200.
 * @param {MediumToDownload[]} media
 * @param {Logger} logger
 * @returns {Promise<MediumToDownload[]>}
 */
export async function mustFilterOutdatedMedia(media, logger) {
  /** @type {Error[]} */
  const headErrors = [];
  /** @type {{mediumToDownload: MediumToDownload, response: Response | null}[]} */
  const possiblyOutdatedMedia = await PromiseUtil.allParallel(
    media.map((mediumToDownload) => async () => {
      /** @type {Response | null} */
      let response = null;
      try {
        response = await headRequestMedium(mediumToDownload.medium);
        if (response.status !== 200) {
          if (response.status !== 304) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            // V tomto případě nechceme posílat stack trace.
            error.stack = '';
            headErrors.push(error);
          }
          response = null;
        }
      } catch (err) {
        const headError = new Error(
          `HEAD request failed: ${/** @type {Error} */ (err).message}`
        );
        // V tomto případě nechceme posílat stack trace.
        headError.stack = '';
        headErrors.push(headError);
      }
      return { mediumToDownload, response };
    })
  ).finally(() => {
    if (headErrors.length > 0) {
      logger.error(new AggregateError(headErrors, 'some HEAD requests failed'));
    }
  });

  return possiblyOutdatedMedia
    .filter(({ response }) => response !== null)
    .map(({ mediumToDownload }) => mediumToDownload);
}

/**
 * Zjistí, jestli je potřeba uvolnit místo v úložišti.
 * @param {number} fileSize
 * @param {FileSystemUnit} fileSystemUnit
 * @returns {Promise<boolean>} True, pokud je obsazené více než 90 % úložiště
 * nebo velikost souboru je vetší než je volné místo v úložišti.
 */
export async function shouldCleanSpace(fileSize, fileSystemUnit) {
  const detected = await FileSystemSpaceDetector.detect(
    fileSystemUnit,
    fileSize
  );
  const { total, used } = await fileSystemUnit.getInfo();
  return !detected || used / total >= 0.9;
}
